// @givmo/app-admin — dashboard area chart (presentational SVG, no deps).
//
// A tasteful gradient-filled area + line chart for the dashboard "Donation volume" card. Pure SVG,
// brand-tinted gradient fill, a crisp brand stroke, soft gridlines, and an end-point dot. No
// charting library (keeps the bundle lean).
//
// Supports TWO optional extra series, and they mean different things:
//   `comparison` — a PRIOR-PERIOD overlay of the same measure, drawn muted + dashed + unfilled. Dashing
//                  is what says "a different period", so it stays reserved for that.
//   `secondary`  — a DIFFERENT measure over the SAME period (EP-WEB8/§131: company-direct giving beside
//                  matched volume), drawn as a solid green line with no fill. Solid says "now"; the
//                  distinct hue says "a different thing".
//
// EVERY series is normalized against the COMBINED max, so the lines are directly comparable and a small
// series never gets visually inflated to fill the box. Single-series callers are unaffected.

export interface AreaChartProps {
  /** Primary series values (raw — normalized internally against the combined max). */
  data: number[];
  /** Optional comparison series (e.g. prior year) — rendered as a muted dashed line, no fill. */
  comparison?: number[];
  /**
   * Optional SECOND MEASURE over the same period — a solid green line, no fill. Same length as `data`.
   * Not a period comparison: pass a prior period as `comparison` so the dashed styling keeps meaning
   * "another period" rather than becoming a generic "extra line".
   */
  secondary?: number[];
  /** X-axis labels aligned to data points ('' = unlabeled tick). */
  labels?: string[];
  /** Accessible description of what the chart shows. */
  ariaLabel?: string;
  className?: string;
  height?: number;
}

export function AreaChart({
  data,
  comparison,
  secondary,
  labels,
  ariaLabel = 'Donation volume',
  className,
  height = 220,
}: AreaChartProps) {
  const width = 720;
  const padX = 8;
  const padTop = 12;
  const padBottom = 28;
  const innerW = width - padX * 2;
  const innerH = height - padTop - padBottom;

  // Normalize EVERY series against the combined max so the lines are comparable. Guard a flat/empty
  // series (max 0) so we never divide by zero.
  const hasComparison = Array.isArray(comparison) && comparison.length > 0;
  const hasSecondary = Array.isArray(secondary) && secondary.length > 0;
  const max = Math.max(
    1,
    ...data,
    ...(hasComparison ? comparison : []),
    ...(hasSecondary ? secondary : []),
  );

  const toPoints = (series: number[]) =>
    series.map((v, i) => {
      const x = padX + (series.length <= 1 ? innerW / 2 : (i / (series.length - 1)) * innerW);
      const y = padTop + innerH - (v / max) * innerH;
      return { x, y };
    });

  const points = toPoints(data);
  const comparisonPoints = hasComparison ? toPoints(comparison) : [];
  const secondaryPoints = hasSecondary ? toPoints(secondary) : [];

  const toLinePath = (pts: { x: number; y: number }[]) =>
    pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');

  const linePath = toLinePath(points);
  const areaPath =
    points.length > 0
      ? `${linePath} L ${(padX + innerW).toFixed(1)} ${(padTop + innerH).toFixed(1)} ` +
        `L ${padX.toFixed(1)} ${(padTop + innerH).toFixed(1)} Z`
      : '';
  const comparisonPath = toLinePath(comparisonPoints);
  const secondaryPath = toLinePath(secondaryPoints);

  const gridLines = [0, 0.25, 0.5, 0.75, 1].map((t) => padTop + innerH * t);
  const last = points[points.length - 1];

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      role="img"
      aria-label={ariaLabel}
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="area-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--color-brand-500)" stopOpacity="0.20" />
          <stop offset="100%" stopColor="var(--color-brand-500)" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Gridlines */}
      {gridLines.map((y, i) => (
        <line
          key={i}
          x1={padX}
          x2={width - padX}
          y1={y}
          y2={y}
          stroke="var(--color-border-subtle)"
          strokeWidth="1"
        />
      ))}

      {/* Prior-year overlay: a muted, dashed line, NO fill (drawn first so it sits behind). */}
      {hasComparison && comparisonPath !== '' && (
        <path
          d={comparisonPath}
          fill="none"
          stroke="var(--color-text-tertiary)"
          strokeWidth="2"
          strokeDasharray="5 5"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.7"
          vectorEffect="non-scaling-stroke"
        />
      )}

      {/* Current-year area + line */}
      {areaPath !== '' && <path d={areaPath} fill="url(#area-fill)" />}
      <path
        d={linePath}
        fill="none"
        stroke="var(--color-brand-600)"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />

      {/* Second measure, same period: solid + unfilled, so it reads as "now" but not as the headline
          area. Drawn after the primary line so a crossing point stays legible. */}
      {hasSecondary && secondaryPath !== '' && (
        <path
          d={secondaryPath}
          fill="none"
          stroke="var(--color-success-600)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      )}

      {/* End-point marker */}
      {last && (
        <circle
          cx={last.x}
          cy={last.y}
          r="5"
          fill="var(--color-surface)"
          stroke="var(--color-brand-600)"
          strokeWidth="2.5"
          vectorEffect="non-scaling-stroke"
        />
      )}

      {/* X labels */}
      {labels?.map((label, i) =>
        label ? (
          <text
            key={i}
            x={points[i]?.x ?? 0}
            y={height - 8}
            textAnchor="middle"
            className="font-sans"
            fontSize="11"
            fill="var(--color-text-tertiary)"
          >
            {label}
          </text>
        ) : null,
      )}
    </svg>
  );
}
